import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth.store';
import { useThreatsRealtimeStore } from '@/store/threatsRealtime.store';
import { useAiSessionRealtimeStore } from '@/store/aiSessionRealtime.store';
import { endpointKeys } from '@/api/endpoints';
import { aiSessionKeys, type EndpointSummaryResult } from '@/api/ai-sessions';
import { mcpKeys } from '@/api/mcps';
import { analyticsKeys } from '@/api/analytics';
import { notificationKeys } from '@/api/notifications';
import type {
  AiSession,
  Endpoint,
  EndpointStatus,
  Mcp,
  PaginatedResponse,
} from '@/types/api';
import type { NotificationDto } from '@/types/notification';

interface DetectionCreatedEvent {
  eventId: string;
  detection: {
    id: string;
    title: string;
    severity: string;
    verdict: string;
    endpointId: string;
    confidence: number;
    createdAt: string;
  };
  timestamp: string;
}

interface AlertCreatedEvent {
  eventId: string;
  alert: {
    id: string;
    title: string;
    severity: string;
    verdict: string;
    endpointId: string;
    confidence: number;
    createdAt: string;
  };
  timestamp: string;
}

// ── Endpoint / AI session / MCP realtime payloads ───────────────────────────
// These carry enough data to patch the React Query cache directly — no
// refetch — matching the shapes the backend broadcasts (see
// backend/src/common/events/edr-events.ts).

interface EndpointStatusEvent {
  eventId: string;
  endpoint: {
    id: string;
    hostname: string;
    status: EndpointStatus;
    riskScore: number;
    lastSeenAt: string;
  };
  timestamp: string;
}

interface EndpointRegisteredEvent {
  eventId: string;
  endpoint: {
    id: string;
    hostname: string;
    ipAddress: string;
    os: string;
    agentVersion: string;
    status: EndpointStatus;
    mode: 'active' | 'passive' | 'off';
    riskScore: number;
    lastSeenAt: string;
    enrolledAt: string;
  };
  isNew: boolean;
  timestamp: string;
}

interface SessionUpdateEvent {
  eventId: string;
  session: {
    id: string;
    sessionId: string;
    endpointId: string;
    endpointHostname: string;
    status: 'active' | 'inactive' | 'suspended' | 'ended';
    tokenCount: number;
    detectionCount: number;
    alertCount: number;
    createdAt: string;
    lastActivityTime: string | null;
  };
  endpointSummary: {
    endpointId: string;
    endpointHostname: string;
    sessionCount: number;
    tokenCount: number;
    activeCount: number;
    detectionCount: number;
    alertCount: number;
    lastActivity: string | null;
  };
  timestamp: string;
}

type McpChangedEvent =
  | { action: 'created' | 'updated'; mcp: Mcp }
  | { action: 'deleted'; id: string };

function getBaseUrl(): string {
  const viteUrl = import.meta.env.VITE_API_URL;
  if (viteUrl && typeof viteUrl === 'string') {
    try {
      const parsed = new URL(viteUrl);
      return `${parsed.protocol}//${parsed.host}`;
    } catch {
      // fall through to default
    }
  }
  return '';
}

export function useRealtimeDetections(): { isConnected: boolean } {
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const queryClient = useQueryClient();
  const { token } = useAuthStore.getState();
  const { registerAlert, registerViolation } = useThreatsRealtimeStore.getState();
  const { registerAlerts: registerSessionAlerts, registerViolations: registerSessionViolations } = useAiSessionRealtimeStore.getState();

  useEffect(() => {
    if (!token) return;

    const baseUrl = getBaseUrl();

    const socket = io(`${baseUrl}/realtime`, {
      autoConnect: false,
      transports: ['websocket', 'polling'],
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('auth', { token });
    });

    socket.on('auth:ok', () => {
      console.log('[realtime] authenticated and connected');
      setIsConnected(true);
    });

    socket.on('auth:error', (err: unknown) => {
      console.error('[realtime] authentication error', err);
      setIsConnected(false);
    });

    socket.on('detection:new', (_event: DetectionCreatedEvent) => {
      registerViolation();
      queryClient.invalidateQueries({ queryKey: analyticsKeys.detectionStats });
    });

    socket.on('alert:new', (_event: AlertCreatedEvent) => {
      registerAlert();
      queryClient.invalidateQueries({ queryKey: analyticsKeys.detectionStats });
    });

    // ── Notifications table — any row created server-side shows up here
    // immediately instead of waiting on the bell's 30s poll. The bell badge
    // is scoped to persisted notification rows only (currently just
    // endpoint_offline), so this is the only event that refreshes it.
    socket.on('notification:new', (_notification: NotificationDto) => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    });

    // ── Endpoint status/registration — patch the cache directly, no refetch ──
    socket.on('endpoint:status', (event: EndpointStatusEvent) => {
      const { endpoint } = event;

      queryClient.setQueryData<Endpoint | undefined>(
        endpointKeys.detail(endpoint.id),
        (old) => (old ? { ...old, ...endpoint } : old),
      );

      queryClient.setQueriesData<PaginatedResponse<Endpoint> | undefined>(
        { queryKey: [...endpointKeys.all, 'list'] },
        (old) => {
          if (!old) return old;
          // Lists fetched with excludeUninstalled no longer want this row once
          // it goes uninstalled — drop it rather than merging in place, since
          // the cache has no way to know which cached lists applied that filter.
          if (endpoint.status === 'uninstalled') {
            const hadIt = old.data.some((e) => e.id === endpoint.id);
            if (!hadIt) return old;
            return {
              ...old,
              data: old.data.filter((e) => e.id !== endpoint.id),
              total: Math.max(0, old.total - 1),
            };
          }
          return {
            ...old,
            data: old.data.map((e) => (e.id === endpoint.id ? { ...e, ...endpoint } : e)),
          };
        },
      );
    });

    socket.on('endpoint:registered', (event: EndpointRegisteredEvent) => {
      const { endpoint, isNew } = event;

      queryClient.setQueryData<Endpoint | undefined>(
        endpointKeys.detail(endpoint.id),
        (old) => (old ? { ...old, ...endpoint } : old),
      );

      queryClient.setQueriesData<PaginatedResponse<Endpoint> | undefined>(
        { queryKey: [...endpointKeys.all, 'list'] },
        (old) => {
          if (!old) return old;
          const exists = old.data.some((e) => e.id === endpoint.id);
          if (exists) {
            return { ...old, data: old.data.map((e) => (e.id === endpoint.id ? { ...e, ...endpoint } : e)) };
          }
          if (!isNew) return old; // re-registration of an endpoint this page hasn't loaded — nothing to patch
          return {
            ...old,
            data: [{ ...endpoint, osVersion: '', policyVersion: '', tags: [] }, ...old.data],
            total: old.total + 1,
          };
        },
      );
    });

    // ── AI session updates — patch the per-session + per-endpoint rollup ────
    socket.on('session:update', (event: SessionUpdateEvent) => {
      const { session, endpointSummary } = event;

      // The session-detail page (AiSessionDetailPage) shows the actual
      // alerts/detections arrays — not just their counts — for the Alerts and
      // Violations tabs (and their badge counts). Those arrays don't come
      // through this event, so a new alert/violation attaching to this
      // session can't be cache-patched directly. Compare this event's fresh
      // counts against whatever's currently cached (if the page has been
      // opened before) to work out how many items are new, so the "+N"
      // blink on the tab the user isn't currently viewing shows an accurate
      // number the moment it happens, before the refetch below even lands.
      const previousDetail = queryClient.getQueryData<AiSession>(aiSessionKeys.detail(session.id));
      if (previousDetail) {
        const violationDelta = session.detectionCount - previousDetail.detections.length;
        const alertDelta = session.alertCount - previousDetail.alerts.length;
        if (violationDelta > 0) registerSessionViolations(session.id, violationDelta);
        if (alertDelta > 0) registerSessionAlerts(session.id, alertDelta);
      }

      const patchSession = (s: AiSession): AiSession => ({
        ...s,
        status: session.status,
        tokenCount: session.tokenCount,
        totalTokens: session.tokenCount,
        createdAt: session.createdAt,
        lastActivityTime: session.lastActivityTime ?? s.lastActivityTime,
      });

      queryClient.setQueryData<AiSession | undefined>(
        aiSessionKeys.detail(session.id),
        (old) => (old ? patchSession(old) : old),
      );

      queryClient.setQueriesData<PaginatedResponse<AiSession> | undefined>(
        { queryKey: [...aiSessionKeys.all, 'list'] },
        (old) => {
          if (!old) return old;
          return { ...old, data: old.data.map((s) => (s.id === session.id ? patchSession(s) : s)) };
        },
      );


      queryClient.invalidateQueries({ queryKey: aiSessionKeys.detail(session.id) });

      const summaryPatch = { ...endpointSummary, lastActivity: endpointSummary.lastActivity ?? undefined };
      queryClient.setQueriesData<EndpointSummaryResult | undefined>(
        { queryKey: [...aiSessionKeys.all, 'endpoint-summary'] },
        (old) => {
          if (!old) return old;
          const idx = old.data.findIndex((g) => g.endpointId === summaryPatch.endpointId);
          if (idx === -1) return old;
          return { ...old, data: old.data.map((g, i) => (i === idx ? { ...g, ...summaryPatch } : g)) };
        },
      );
      queryClient.invalidateQueries({ queryKey: [...aiSessionKeys.all, 'endpoint-summary'] });
    });

    // ── MCP create/update/delete — the payload already carries the full
    //    record, so this only ever patches the cache, never refetches ───────
    socket.on('mcp:changed', (event: McpChangedEvent) => {
      if (event.action === 'deleted') {
        queryClient.setQueriesData<PaginatedResponse<Mcp> | undefined>(
          { queryKey: [...mcpKeys.all, 'list'] },
          (old) =>
            old
              ? { ...old, data: old.data.filter((m) => m.id !== event.id), total: Math.max(0, old.total - 1) }
              : old,
        );
        queryClient.removeQueries({ queryKey: mcpKeys.detail(event.id) });
        return;
      }

      const { mcp } = event;
      queryClient.setQueryData<Mcp | undefined>(mcpKeys.detail(mcp.id), (old) => (old ? { ...old, ...mcp } : old));

      queryClient.setQueriesData<PaginatedResponse<Mcp> | undefined>(
        { queryKey: [...mcpKeys.all, 'list'] },
        (old) => {
          if (!old) return old;
          const exists = old.data.some((m) => m.id === mcp.id);
          if (exists) {
            return { ...old, data: old.data.map((m) => (m.id === mcp.id ? { ...m, ...mcp } : m)) };
          }
          if (event.action !== 'created') return old;
          return { ...old, data: [mcp, ...old.data], total: old.total + 1 };
        },
      );
    });

    socket.on('disconnect', () => {
      setIsConnected(false);
    });

    socket.connect();

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token, registerAlert, registerViolation, registerSessionAlerts, registerSessionViolations, queryClient]);

  return { isConnected };
}
